import React, { useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Boxes, Inbox, Truck, RefreshCw, Wrench, Undo2, MapPin, Users, LogOut, FileText, BarChart3, Scale, Map, Activity, PackageCheck,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, roles: "all" },
  { to: "/ledger", label: "Stock Ledger", icon: Boxes, roles: "all" },
  { to: "/inbox", label: "AX Inbox (IP1/IP2)", icon: Inbox, roles: "all" },
  { to: "/distribution", label: "Distribution", icon: Truck, roles: ["warehouse_manager", "operations_controller"] },
  { to: "/transfers", label: "Regional Transfers", icon: PackageCheck, roles: ["warehouse_manager", "regional_manager", "operations_controller"] },
  { to: "/replenishment", label: "Replenishment", icon: RefreshCw, roles: "all" },
  { to: "/installations", label: "Installations", icon: Wrench, roles: ["fitment_technician", "operations_controller"] },
  { to: "/returns", label: "Returns", icon: Undo2, roles: "all" },
  { to: "/reports", label: "Reporting & KPIs", icon: BarChart3, roles: ["operations_controller", "finance", "warehouse_manager", "regional_manager"] },
  { to: "/reconciliation", label: "AX Reconciliation", icon: Scale, roles: ["operations_controller", "finance", "warehouse_manager", "regional_manager"] },
  { to: "/map", label: "Stock Map", icon: MapPin, roles: ["operations_controller", "finance", "warehouse_manager", "regional_manager"] },
  { to: "/regions", label: "Regional Heatmap", icon: Map, roles: ["operations_controller", "finance", "warehouse_manager", "regional_manager"] },
  { to: "/locations", label: "Locations", icon: MapPin, roles: "all" },
  { to: "/users", label: "Users", icon: Users, roles: ["system_admin"] },
  { to: "/activity", label: "Activity Log", icon: Activity, roles: ["system_admin"] },
];

const SCREEN_LABELS = NAV.reduce((m, n) => ({ ...m, [n.to]: n.label }), {});

const allowed = (roles, role) =>
  roles === "all" || role === "system_admin" || roles.includes(role);

export default function PlatformLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const role = user?.role;
  const lastTracked = useRef(null);

  useEffect(() => {
    const path = loc.pathname;
    if (lastTracked.current === path) return;
    lastTracked.current = path;
    const screen = SCREEN_LABELS[path] || path;
    api.post("/activity/screen", { screen, path }).catch(() => {});
  }, [loc.pathname]);

  return (
    <div className="flex min-h-screen bg-[#F4F5F7]">
      <aside className="w-64 shrink-0 bg-[#0B2545] text-white flex flex-col h-screen sticky top-0">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="font-head text-xl font-bold tracking-widest">NETSTA<span style={{ color: "#00857C" }}>R</span></div>
          <div className="overline text-white/40 mt-1">Consignment Stock</div>
        </div>
        <nav className="flex-1 overflow-y-auto thin-scroll py-3">
          {NAV.filter((n) => allowed(n.roles, role)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm font-head transition-colors duration-200 ${
                  isActive ? "bg-[#00857C] text-white" : "text-white/75 hover:bg-white/10"
                }`
              }>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
          <a href="/proposal" className="flex items-center gap-3 px-5 py-2.5 text-sm font-head text-white/50 hover:bg-white/10 mt-2">
            <FileText className="h-4 w-4" /> Proposal Docs
          </a>
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="text-sm font-head text-white">{user?.name}</div>
          <div className="text-[11px] text-white/50 mb-3">{user?.role_label}</div>
          <button data-testid="logout-button" onClick={() => { logout(); nav("/login"); }}
            className="flex items-center gap-2 text-xs text-white/70 hover:text-[#00857C]">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-8 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
